
/* Config
 * -------------------------------------------------------------------------- */

/**
 * Brackets are always `]min,max]` (e.g. 0..500.00, then 500.01..1000.00).
 *
 * - Brackets `maxGrossAmount` must be in increasing order
 * - Last bracket must contain no `maxGrossAmount`
 */
export const VatRefundBrackets: VatRefundBracketList = [
  { maxGrossAmount: 500, refundRate: 72.5 },
  { maxGrossAmount: 1000, refundRate: 77.5 },
  { maxGrossAmount: 2500, refundRate: 80.0 },
  { maxGrossAmount: 3500, refundRate: 85.0 },
  { refundRate: 90.0 },
];


export const CountryVat = {
  FR: 20.0,
  ES: 21.0,
  BE: 21.0,
};


/* Types
 * -------------------------------------------------------------------------- */

export type Country = keyof typeof CountryVat;

export type VatRefundBracketList = [
  ...BoundedVatRefundBracket[],
  LastVatRefundBracket
];

export type VatRefundBracket = (
  | BoundedVatRefundBracket
  | LastVatRefundBracket
);

type BoundedVatRefundBracket = {
  maxGrossAmount: number;
  refundRate: number;
};

type LastVatRefundBracket = {
  refundRate: number;
};


/* Interface
 * -------------------------------------------------------------------------- */

/**
 * There are two obvious formula for this:
 *
 * - let `p` the gross price amount
 * - let `v` the VAT multiplier
 * - let `x` the refundable proportion of the VAT
 * - let `r` the refundable amount
 *
 * We can find via:
 *
 * - A: `r = p * x * v / (1 + v)`
 * - B: `r = x * (p - p / (1 + v))`
 *
 * Here we will use the last as it's the most obvious when re-using the VAT amount
 *
 * @version 2025-04-20 - We will use the formula B to reuse the computed VAT amount (as it's displayed in other parts).
 */
export function getRefundableAmount(country: Country, grossAmount: number) {
  const refundRate = findRefundableProportionOfVat(grossAmount);
  const vatAmount = getVatAmount(country, grossAmount);
  const refundableAmount = vatAmount * refundRate;

  return Math.max(0, refundableAmount);
}


/**
 * Get the amount of VAT part of the gross price.
 *
 * @version 2025-04-20 - Fixed VAT rate calculation from `vatRate * grossPrice` to `grossPrice - (grossPrice * (vatRate - 1))`
 */
export function getVatAmount(country: Country, grossAmount: number) {
  const vatFactor = getGrossPriceFactor(country);
  const netAmount = grossAmount / vatFactor;
  const vatAmount = grossAmount - netAmount;

  return Math.max(0, vatAmount);
}


export function findRefundableProportionOfVat(grossAmount: number) {
  const bracket = VatRefundBrackets.find(bracket => (
    isBoundedBracket(bracket)
      ? grossAmount <= bracket.maxGrossAmount
      : true
  ));

  if (!bracket) {
    throw new Error("Brackets are not defined correctly, the last bracket purchase price must be null");
  }

  return bracket.refundRate / 100;
}


/* Private functions
 * -------------------------------------------------------------------------- */

/**
 * Returns the VAT as factor of the net amount (e.g. 20% VAT -> 1.20 factor)
 */
function getGrossPriceFactor(country: Country) {
	return 1 + getCountryVatMultiplier(country);
}


/**
 * Returns the VAT as the multipliable proportion (e.g. 20% VAT -> 0.20 multiplier)
 */
function getCountryVatMultiplier(country: Country) {
	return CountryVat[country] / 100;
}


function isBoundedBracket(bracket: VatRefundBracket): bracket is BoundedVatRefundBracket {
  return "maxGrossAmount" in bracket;
}
